/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';

import { usePermissions, UserContext } from '../../../../providers/userContextProvider';
import { usePageState } from '../../../../util/usePageState';
import { FETCH_SUB_TRADES, FETCH_TRADES } from './tradeOrders.queries';
import { Box, Skeleton, Typography } from '../../../1-primative';
import {
  Table, TableRow, TableCell, TableBody, MenuItem, TableHeadCell, Card, CardContent, SelectField, Pagination,
} from '../../../2-component';
import { FilterModal, SecuritySelect } from '../../../3-pattern';
import { PageObjectType } from '../../../5-page';
import { translateBackend } from '../../../../assets/i18n/config';
import NewTrade from './components/newTrade';
import DownloadTrades from './components/downloadTrades';
import { TradeModal } from './components/tradeModal';
import { TradeOrderCell } from './components/tradeOrderCell';

const PAGE_SIZE = 15;

const DEFAULT_TABLE = [
  { label: { en: 'Account' }, type: 'account' as const },
  { label: { en: 'Account Number' }, type: 'accountNumber' as const },
  { label: { en: 'Symbol' }, type: 'symbol' as const },
  { label: { en: 'Type' }, type: 'type' as const },
  { label: { en: 'Actual Price' }, type: 'actualPrice' as const },
  { label: { en: 'Actual Quantity' }, type: 'actualQuantity' as const },
  { label: { en: 'Expected Price' }, type: 'expectedPrice' as const },
  { label: { en: 'Expected Quantity' }, type: 'expectedQuantity' as const },
  { label: { en: 'State' }, type: 'state' as const },
  { label: { en: 'Created' }, type: 'createdAt' as const },
  { label: { en: 'Reconciled' }, type: 'reconciledAt' as const },
];

export enum TradeStates {
  INITIATED = 'INITIATED',
  PROCESSING = 'PROCESSING',
  RECONCILED = 'RECONCILED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export const TradeOrders = ({ objectType, objectId, options }: { objectType: PageObjectType, objectId: string, options: any }) => {
  const { t } = useTranslation(['components', 'transfer']);
  const { activeOrganization } = useContext(UserContext);
  const { permissions } = usePermissions();
  const [page, setPage] = usePageState(1, 'tra/page');
  const [state, setState] = usePageState('any', 'tra/state');

  const [activeTrade, setActiveTrade] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [trades, setTrades] = useState<{ trades: any[], totalCount: number }>();
  const [perPage, setPerPage] = useState<number>(PAGE_SIZE);
  const [financialProductId, setFinancialProductId] = useState<string>('ANY');

  const showAddbutton = permissions.includes('write:trade_basic') && options.canAddNewTradeOrder;
  const showDownloadbutton = permissions.includes('read:api_exports') && options.enableDownload;
  const isTrades = ![PageObjectType.GOAL, PageObjectType.SUB_ACCOUNT].includes(objectType);
  const totalCount = trades?.totalCount || 0;
  const showPagination = totalCount > PAGE_SIZE;

  const queryFilter = {
    organizationId: activeOrganization.id,
    financialProductId: financialProductId === 'ANY' ? undefined : financialProductId,
    ...(state !== 'any' ? { [isTrades ? 'tradeState' : 'subTradeState']: state } : {}),
    ...(options?.showOnlyPendingTrades ? { [isTrades ? 'tradeStates' : 'subTradeStates']: [TradeStates.INITIATED, TradeStates.PROCESSING] } : {}),
  };

  let filter: any = {};

  switch (objectType) {
    case PageObjectType.INDIVIDUAL:
    case PageObjectType.NON_INDIVIDUAL:
      filter = { userId: objectId };
      break;
    case PageObjectType.HOUSEHOLD:
      filter = { clientGroupId: objectId };
      break;
    case PageObjectType.ACCOUNT:
      filter = { accountId: objectId };
      break;
    case PageObjectType.SUB_ACCOUNT:
      filter = { subAccountId: objectId };
      break;
    case PageObjectType.GOAL:
      filter = { goalId: objectId };
      break;
    default:
      break;
  }

  const { loading, previousData, refetch } = useQuery(isTrades ? FETCH_TRADES(permissions) : FETCH_SUB_TRADES(permissions), {
    variables: {
      input: {
        filter: { ...filter, ...queryFilter },
        pagination: {
          sortField: 'createdAt', sortDesc: false, perPage, offSet: (page - 1) * perPage,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    skip: !permissions.includes('read:trade_basic'),
    onCompleted: (data) => {
      if (isTrades) {
        setTrades({ trades: data.fetchTrades?.trades, totalCount: data.fetchTrades?.totalCount });
      } else {
        setTrades({ trades: data.fetchSubTrades?.subTrades, totalCount: data.fetchSubTrades?.totalCount });
      }

      if (activeTrade?.id) {
        setActiveTrade((isTrades ? data.fetchTrades.trades : data.fetchSubTrades.subTrades).find((x: any) => x.id === activeTrade.id));
      }
    },
  });

  const tradeStates = [
    (options.filterForInitiated && TradeStates.INITIATED),
    (options.filterForProcessing && TradeStates.PROCESSING),
    (options.filterForReconciled && TradeStates.RECONCILED),
    (options.filterForFailed && TradeStates.FAILED),
    (options.filterForCanceled && TradeStates.CANCELED),
  ].filter(Boolean);

  useEffect(() => function cleanupOnUnmount() {
    setState('any');
    setPage(1);
  }, []);

  if (!permissions.includes('read:trade_basic')) return <></>;

  const filterExists = (!!state && state !== 'any') || (!!financialProductId && financialProductId !== 'ANY');

  return (
    <Box>
      {options.customTitle && <Typography variant='headingSmall' sx={{ mb: 2 }}>{translateBackend(options.customTitle)}</Typography>}
      <Card>
        {(showAddbutton || showDownloadbutton || options.enableFilter) && (
          <CardContent>
            <Box display='flex' justifyContent='end' alignItems='end'>
              <Box display='flex'>
                {options.enableFilter && (
                  <FilterModal sx={{ mr: 1, ml: 1 }} filterExists={filterExists}>
                    <SelectField
                      value={state}
                      label={t('tradeTable.state')}
                      onChange={(e: any) => setState(e.target.value)}
                      size='medium'
                      fullWidth
                      placeholder={t('any')}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value={'any'}>{t('any')}</MenuItem>
                      {tradeStates.map((x: any) => <MenuItem value={x} key={x}>{t(`transfer:states.${x}`)}</MenuItem>)}
                    </SelectField>
                    <SecuritySelect withAny
                      value={financialProductId ?? 'ANY'}
                      setSecurity={(e: any) => setFinancialProductId(e?.id ?? e)}
                      label={t('components:security')}
                      organizationId={options.organizationId}
                    />
                  </FilterModal>
                )}
                {showDownloadbutton && <DownloadTrades filter={filter} queryFilter={queryFilter} isTrades={isTrades} />}
                {showAddbutton && (
                  <NewTrade afterCreate={() => refetch()} userId={filter.userId} userIds={filter.userIds}
                    {...filter.userId && { forObject: 'USER', forId: filter.userId }}
                    {...filter.clientGroupId && { forObject: 'CLIENT_GROUP', forId: filter.clientGroupId }}
                    {...filter.accountId && { forObject: 'ACCOUNT', forId: filter.accountId }}
                    {...filter.goalId && { forObject: 'GOAL', forId: filter.goalId }}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table aria-label="table">
            <TableBody>
              <TableRow>
                {(options.table || DEFAULT_TABLE).map((x: any, idx: number) => <TableHeadCell key={x} isFirst={idx === 0}>{translateBackend(x.label)}</TableHeadCell>)}
              </TableRow>
              {loading && !previousData && [...Array(15)].map((x: any, i: number) => (
                <TableRow key={i}>
                  {(options.table || DEFAULT_TABLE).map((_: any, idx: number) => <TableCell dense isFirst={idx === 0}><Skeleton width='100%' height='16px'/></TableCell>)}
                </TableRow>
              ))}
              {trades?.trades.map((trade: any) => (
                <TableRow hover pointer key={trade.id} onClick={() => {
                  setActiveTrade(trade);
                  setModalOpen(true);
                }}>
                  {(options.table || DEFAULT_TABLE).map((x: any, i: number) => <TradeOrderCell key={i} trade={trade} isFirst={i === 0} type={x.type} isTrades={isTrades} />) }
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {showPagination && (
          <Box sx={{ px: 2, py: 1 }}>
            <Pagination
              size='small'
              page={page}
              total={totalCount}
              perPage={perPage}
              count={Math.ceil(totalCount / perPage)}
              onChange={(e, newPage) => setPage(newPage)}
              onChangePerPage={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
            />
          </Box>
        )}
        <TradeModal options={options} trade={activeTrade} isTrades={isTrades} open={modalOpen} setOpen={setModalOpen} onTransition={refetch}/>
      </Card>
    </Box>
  );
};

export default TradeOrders;
